import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { Controller, Control } from "react-hook-form";
import { ttsGenerateUrl } from "../../../config";

type MediaType = 'image' | 'audio' | 'tts' | 'video';

interface MediaUploadProps {
    tabIndex?: number;
    viewMode?: boolean;
    mediaType: MediaType | null | undefined;
    mediaFile: FileList | string | undefined;
    control: Control<any>;
    title?: string;
    language?: string;
    setSharedData: (data: any) => void;
    onMediaTypeChange: (type: MediaType) => void;
    onMediaFileChange: (file: FileList | null) => void;
    onUploadSuccess: (url: string, campaignId: string, fileName?: string) => void;
    onTtsGenerated?: (language: string, file: File) => void;
    onAudioFileChange?: (language: string, file: File) => void;
    onVideoFileChange?: (language: string, file: File) => void;
    onImageFileChange?: (file: FileList | null) => void;
    imageFile?: FileList | string | undefined;
    onError: (message: string) => void;
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
    tabIndex = 0,
    viewMode = false,
    mediaType,
    mediaFile,
    control,
    language = 'en',
    setSharedData,
    onMediaTypeChange,
    onMediaFileChange,
    onError,
    onTtsGenerated,
    onAudioFileChange,
    onVideoFileChange,
    onImageFileChange,
    imageFile,
}) => {
    
    const normalizedMediaType: MediaType | null = (mediaType as MediaType) || null;
    const safeLang = language.toLowerCase();
    
    // Media state by language
    const [mediaByLanguage, setMediaByLanguage] = useState<Record<string, {
        file?: File;
        url?: string;
        label?: string;
        ttsText?: string;
    }>>({});
    
    // Store blob URLs per language for video (to prevent ERR_FILE_NOT_FOUND)
    const [videoBlobUrls, setVideoBlobUrls] = useState<Record<string, string>>({});
    // Store blob URLs per language for image (to prevent ERR_FILE_NOT_FOUND)
    const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
    
    const [isConvertingTts, setIsConvertingTts] = useState(false);
    const [currentTtsText, setCurrentTtsText] = useState('');
    const audioRef = useRef<HTMLAudioElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    // State for view mode audio/video URL (base64)
    const [viewModeAudioUrl, setViewModeAudioUrl] = useState<string | null>(null);
    const [viewModeVideoUrl, setViewModeVideoUrl] = useState<string | null>(null);
    // State for media type change confirmation dialog
    const [showMediaTypeChangeDialog, setShowMediaTypeChangeDialog] = useState(false);
    const [pendingMediaType, setPendingMediaType] = useState<MediaType | null>(null);
    const [pendingFieldOnChange, setPendingFieldOnChange] = useState<((value: MediaType) => void) | null>(null);

    // Get current media for active language
    const [currentMedia, setCurrentMedia] = useState<{
        file?: File;
        url?: string;
        label?: string;
        ttsText?: string;
    }>({});

    // Helper function to convert File to base64
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to convert file to base64'));
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Handle TTS conversion
    const handleConvertToSpeech = async () => {
        if (!currentTtsText.trim()) {
            onError("Please enter text to convert to speech");
            return;
        }

        try {
            setIsConvertingTts(true);

            const response = await fetch(ttsGenerateUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: currentTtsText, language }),
            });
            
            if (!response.ok) throw new Error("TTS request failed");

            const data = await response.json();
            if (!data?.data_url) throw new Error("Invalid TTS response");

            const generatedUrl = data.data_url;
            
            // Update state with TTS data
            setMediaByLanguage(prev => ({
                ...prev,
                [safeLang]: {
                    ...prev[safeLang],
                    url: generatedUrl,
                    label: `TTS Audio (${language})`,
                    ttsText: currentTtsText
                }
            }));

            // Create File from data URL
            const responseBlob = await fetch(generatedUrl);
            const audioBlob = await responseBlob.blob();
            const mimeType = generatedUrl.match(/data:([^;]+)/)?.[1] || 'audio/wav';
            const fileExt = mimeType.includes('wav') ? 'wav' : 'mp3';
            const file = new File([audioBlob], `tts_${safeLang}.${fileExt}`, { type: mimeType });

            if (onTtsGenerated) {
                onTtsGenerated(safeLang, file);
            }

        } catch (error) {
            console.error("TTS error:", error);
            onError("Failed to convert text to speech");
        } finally {
            setIsConvertingTts(false);
        }
    };

    // Handle file upload
    const handleFileUpload = React.useCallback(async (files: FileList | null) => {
        if (!files || !files[0]) return;
        
        const file = files[0];
        
        if (normalizedMediaType === 'image' || normalizedMediaType === null) {
            // Treat null as image for file upload purposes
            // Image is global, always store in 'en' key regardless of current language
            // Revoke previous blob URL if exists
            if (imageBlobUrls['en']) {
                URL.revokeObjectURL(imageBlobUrls['en']);
            }
            const blobUrl = URL.createObjectURL(file);
            setImageBlobUrls(prev => ({
                ...prev,
                en: blobUrl
            }));
            setMediaByLanguage(prev => ({
                ...prev,
                en: { file, url: blobUrl, label: file.name } // Always store image in 'en' key (global)
            }));
            
            // Update currentMedia for image
            setCurrentMedia({
                file,
                url: blobUrl,
                label: file.name
            });
        } else if (normalizedMediaType === 'audio') {
            // Handle audio file upload per language - convert to base64
            try {
                const base64Url = await fileToBase64(file);
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { file, url: base64Url, label: file.name }
                }));
                
                setCurrentMedia(prev => ({
                    ...prev,
                    file,
                    url: base64Url,
                    label: file.name
                }));
                // Notify parent component about audio file change
                if (onAudioFileChange) {
                    onAudioFileChange(safeLang, file);
                }
            } catch (error) {
                console.error("Error converting audio to base64:", error);
                onError("Failed to process audio file");
                return;
            }
        } else if (normalizedMediaType === 'video') {
            // Handle video file upload per language - use blob URL for preview (not base64 for large files)
            try {
                // Revoke previous blob URL for this language if exists
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                }
                // Use blob URL instead of base64 for video to avoid memory issues with large files
                const blobUrl = URL.createObjectURL(file);
                // Store blob URL in state
                setVideoBlobUrls(prev => ({
                    ...prev,
                    [safeLang]: blobUrl
                }));
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { file, url: blobUrl, label: file.name }
                }));
                
                setCurrentMedia({
                    file,
                    url: blobUrl,
                    label: file.name
                });
                // Notify parent component about video file change
                if (onVideoFileChange) {
                    onVideoFileChange(safeLang, file);
                }
            } catch (error) {
                console.error("Error processing video file:", error);
                onError("Failed to process video file");
                return;
            }
        }
        
        onMediaFileChange(files);
    }, [normalizedMediaType, safeLang, onMediaFileChange, onAudioFileChange, onVideoFileChange, onError, imageBlobUrls, videoBlobUrls]);

    // Sync external mediaFile with internal state
    useEffect(() => {
        if (!mediaFile) return;
        
        if (typeof mediaFile === 'string') {
            const label = normalizedMediaType === 'audio' 
                ? 'Uploaded audio file' 
                : normalizedMediaType === 'video' 
                    ? 'Uploaded video file' 
                    : 'Uploaded file';
            setMediaByLanguage(prev => ({
                ...prev,
                [safeLang]: { ...prev[safeLang], url: mediaFile, label }
            }));
        } else if (mediaFile[0]) {
            // handleFileUpload(mediaFile);
        }
    }, [mediaFile, normalizedMediaType, safeLang, handleFileUpload]);

    // Load TTS text when language changes
    useEffect(() => {
        if (normalizedMediaType === 'tts') {
            setCurrentTtsText(currentMedia.ttsText || '');
        }
    }, [safeLang, normalizedMediaType, currentMedia.ttsText]);
    
    // Sync audio/video files from external source when in view mode or when mediaFile changes
    useEffect(() => {
        if ((normalizedMediaType === 'audio' || normalizedMediaType === 'video') && mediaFile) {
            if (typeof mediaFile === 'string') {
                const label = normalizedMediaType === 'audio' ? 'Uploaded audio file' : 'Uploaded video file';
                setMediaByLanguage(prev => ({
                    ...prev,
                    [safeLang]: { 
                        ...prev[safeLang], 
                        url: mediaFile, 
                        label
                    }
                }));
                // Also update currentMedia immediately for view mode
                if (viewMode) {
                    setCurrentMedia(prev => ({
                        ...prev,
                        url: mediaFile,
                        label
                    }));
                    if (normalizedMediaType === 'audio') {
                        setViewModeAudioUrl(mediaFile);
                    } else if (normalizedMediaType === 'video') {
                        setViewModeVideoUrl(mediaFile);
                    }
                }
            }
        }
    }, [mediaFile, normalizedMediaType, safeLang, viewMode]);

    // Reset audio player on tab/language change for TTS/Audio
    // Use a ref to track the previous URL to avoid unnecessary updates
    const previousAudioUrlRef = useRef<string | undefined>(undefined);
    
    useEffect(() => {
        if (normalizedMediaType !== 'tts' && normalizedMediaType !== 'audio') {
            previousAudioUrlRef.current = undefined;
            return;
        }
        
        // Get current media URL for the active language
        const currentAudioUrl = currentMedia.url;
        
        // Only update if the URL has actually changed
        if (previousAudioUrlRef.current === currentAudioUrl) {
            return;
        }
        
        previousAudioUrlRef.current = currentAudioUrl;
        
        // Update audio element only when URL changes
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            
            if (currentAudioUrl) {
                // Load the audio for current language
                audioRef.current.src = currentAudioUrl;
                audioRef.current.load();
            } else {
                // Clear source if no audio for this language
                audioRef.current.src = '';
                audioRef.current.load();
            }
        }
    }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

    // Reset video player on tab/language change for Video
    const previousVideoUrlRef = useRef<string | undefined>(undefined);
    const previousLangRef = useRef<string | undefined>(undefined);
    
    useEffect(() => {
        if (normalizedMediaType !== 'video') {
            previousVideoUrlRef.current = undefined;
            previousLangRef.current = undefined;
            // Clear video element if not video type
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                videoRef.current.src = '';
                videoRef.current.removeAttribute('src');
                videoRef.current.load();
            }
            return;
        }
        
        // Get current media URL for the active language
        const currentVideoUrl = currentMedia.url;
        const langChanged = previousLangRef.current !== safeLang;
        previousLangRef.current = safeLang;
        
        // Always update when language changes, or when URL changes
        const shouldUpdate = langChanged || previousVideoUrlRef.current !== currentVideoUrl;
        
        if (!shouldUpdate) {
            return;
        }
        
        previousVideoUrlRef.current = currentVideoUrl;
        
        // Update video element when URL or language changes
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            
            if (currentVideoUrl) {
                // Set the video source - the key prop will force remount if needed
                videoRef.current.src = currentVideoUrl;
                videoRef.current.load();
            } else {
                // Clear source if no video for this language - ensure it's completely cleared
                videoRef.current.src = '';
                videoRef.current.removeAttribute('src');
                // Clear all source elements
                const sources = videoRef.current.querySelectorAll('source');
                sources.forEach(source => source.remove());
                videoRef.current.load();
            }
        }
    }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

    // Cleanup blob URLs only on component unmount (not when state changes)
    useEffect(() => {
        return () => {
            // Revoke all video blob URLs on unmount
            Object.values(videoBlobUrls).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            // Revoke all image blob URLs on unmount
            Object.values(imageBlobUrls).forEach(url => {
                if (url?.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on unmount

    useEffect(() => {
        // Check both mediaByLanguage and mediaFile prop for view mode
        // For image type, check globally (from 'en' key or mediaFile prop) since it's global
        let mediaUrl: string | undefined;
        if (normalizedMediaType === 'image') {
            // Image is global, always recreate blob URL from File object when switching tabs
            // This ensures blob URLs are always valid
            const storedFile = mediaByLanguage['en']?.file;
            
            if (storedFile) {
                // Always recreate blob URL from File to ensure it's valid
                // Revoke old blob URL if exists
                if (imageBlobUrls['en']) {
                    URL.revokeObjectURL(imageBlobUrls['en']);
                }
                const blobUrl = URL.createObjectURL(storedFile);
                setImageBlobUrls(prev => ({ ...prev, en: blobUrl }));
                mediaUrl = blobUrl;
            } else if (typeof mediaFile === 'string') {
                mediaUrl = mediaFile;
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                // Create blob URL if not in state
                const blobUrl = URL.createObjectURL(mediaFile[0]);
                setImageBlobUrls(prev => ({ ...prev, en: blobUrl }));
                mediaUrl = blobUrl;
            }
        } else if (normalizedMediaType === 'video') {
            // For video, always recreate blob URL from File object when switching tabs
            // This ensures blob URLs are always valid
            // Only show video if it exists for the current language
            const storedFile = mediaByLanguage[safeLang]?.file;
            
            if (storedFile) {
                // Always recreate blob URL from File to ensure it's valid
                // Revoke old blob URL if exists
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                }
                const blobUrl = URL.createObjectURL(storedFile);
                setVideoBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else if (viewMode && typeof mediaFile === 'string') {
                // In view mode, use mediaFile if it's a string URL
                // This is the video URL for the current language from the API
                mediaUrl = mediaFile;
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                // Create blob URL if not in state (for new uploads)
                const blobUrl = URL.createObjectURL(mediaFile[0]);
                setVideoBlobUrls(prev => ({ ...prev, [safeLang]: blobUrl }));
                mediaUrl = blobUrl;
            } else {
                // No video for this language - explicitly set to undefined
                // Also revoke any existing blob URL for this language
                if (videoBlobUrls[safeLang]) {
                    URL.revokeObjectURL(videoBlobUrls[safeLang]);
                    setVideoBlobUrls(prev => {
                        const newState = { ...prev };
                        delete newState[safeLang];
                        return newState;
                    });
                }
                mediaUrl = undefined;
            }
        } else {
            // For other types (audio, tts), check per language
            mediaUrl = mediaByLanguage[safeLang]?.url || (viewMode && typeof mediaFile === 'string' ? mediaFile : undefined);
        }
        
        if(mediaUrl) {   
            // For image, use 'en' data if available (global), otherwise use current language or create from mediaFile
            let mediaData;
            if (normalizedMediaType === 'image' && mediaByLanguage['en']) {
                mediaData = { ...mediaByLanguage['en'], url: mediaUrl };
            } else if (normalizedMediaType === 'video') {
                // For video, set mediaData if we have a file for this language OR if in view mode with a URL
                if (mediaByLanguage[safeLang]?.file) {
                    mediaData = { ...mediaByLanguage[safeLang], url: mediaUrl };
                } else if (viewMode && typeof mediaFile === 'string' && mediaUrl) {
                    // In view mode, use the URL from mediaFile prop
                    mediaData = { url: mediaUrl, label: 'Uploaded video' };
                } else {
                    // No video for this language - don't set mediaData
                    mediaData = { file: undefined, url: undefined, label: undefined, ttsText: undefined };
                }
            } else {
                mediaData = mediaByLanguage[safeLang] || { url: mediaUrl, label: 'Uploaded file' };
            }
            setCurrentMedia(mediaData);
            if (normalizedMediaType === 'audio' || normalizedMediaType === 'tts') {
                setViewModeAudioUrl(mediaUrl);
            } else if (normalizedMediaType === 'video') {
                setViewModeVideoUrl(mediaUrl);
            }
        }else{
            // Only clear media if it's not image type (image is global and should persist)
            if (normalizedMediaType !== 'image') {
                // Clear current media
                setCurrentMedia({
                    file: undefined,
                    url: undefined,
                    label: undefined,
                    ttsText: undefined
                });
                
                // For video, also clear the video element src to prevent showing previous language's video
                if (normalizedMediaType === 'video' && videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                    videoRef.current.src = '';
                    videoRef.current.load();
                }
                
                setSharedData((prev:any) => ({
                    ...prev,
                    mediaFile: undefined,
                }));

                setViewModeAudioUrl(null);
                setViewModeVideoUrl(null);
            } else {
                // For image type, keep the existing mediaFile even if not found in current language
                // Image is global, so preserve it - use stored blob URL from state
                if (mediaFile) {
                    let imageUrl: string | undefined;
                    if (imageBlobUrls['en']) {
                        imageUrl = imageBlobUrls['en'];
                    } else if (typeof mediaFile === 'string') {
                        imageUrl = mediaFile;
                    } else if (mediaFile instanceof FileList && mediaFile[0]) {
                        // Create blob URL if not in state
                        const blobUrl = URL.createObjectURL(mediaFile[0]);
                        setImageBlobUrls(prev => ({ ...prev, en: blobUrl }));
                        imageUrl = blobUrl;
                    }
                    if (imageUrl) {
                        setCurrentMedia({
                            url: imageUrl,
                            label: typeof mediaFile === 'string' ? 'Uploaded image' : mediaFile[0]?.name || 'Uploaded image'
                        });
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIndex, normalizedMediaType, safeLang, mediaByLanguage, mediaFile, viewMode]);

    // Convert audio/video file to base64 in view mode
    useEffect(() => {
        if (viewMode && mediaFile && (normalizedMediaType === 'tts' || normalizedMediaType === 'audio')) {
            if (typeof mediaFile === 'string') {
                setViewModeAudioUrl(mediaFile);
            } else if (mediaFile instanceof FileList && mediaFile[0]) {
                fileToBase64(mediaFile[0])
                    .then(setViewModeAudioUrl)
                    .catch(error => {
                        console.error("Error converting audio to base64 in view mode:", error);
                        setViewModeAudioUrl(null);
                    });
            } else {
                setViewModeAudioUrl(null);
            }
        } else if (viewMode && normalizedMediaType === 'video') {
            // For video in view mode, the main useEffect handles setting viewModeVideoUrl
            // Don't clear it here - let the main useEffect manage it
        } else {
            // Only clear if not in view mode or not the current media type
            if (!viewMode || (normalizedMediaType !== 'tts' && normalizedMediaType !== 'audio' && normalizedMediaType !== 'video')) {
                setViewModeAudioUrl(null);
                setViewModeVideoUrl(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, mediaFile, normalizedMediaType, safeLang, videoBlobUrls, imageBlobUrls]);

    useEffect(() => {
    }, [currentMedia]);

    const acceptTypes = normalizedMediaType === 'image' 
        ? 'image/*' 
        : normalizedMediaType === 'video' 
            ? 'video/*' 
            : 'audio/*';
    const buttonLabel = normalizedMediaType === 'image' 
        ? 'Upload Image' 
        : normalizedMediaType === 'video' 
            ? 'Upload Video' 
            : 'Upload Audio';

    // View Mode
    if (viewMode) {
        const mediaUrl = typeof mediaFile === 'string' 
            ? mediaFile 
            : normalizedMediaType === 'image' && mediaFile
                ? URL.createObjectURL(mediaFile[0])
                : viewModeAudioUrl || '';

        return (
            <Box mt={2}>
                {/* Show Media Type Selection (disabled) in view mode */}
                <FormControl component="fieldset">
                    <FormLabel component="legend">Select Media Type</FormLabel>
                    <Controller
                        name="mediaType"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup
                                row
                                value={mediaType || ''}
                            >
                                <FormControlLabel 
                                    value="image" 
                                    control={<Radio disabled={true} />} 
                                    label="Image" 
                                />
                                <FormControlLabel 
                                    value="audio" 
                                    control={<Radio disabled={true} />} 
                                    label="Audio" 
                                />
                                <FormControlLabel 
                                    value="video" 
                                    control={<Radio disabled={true} />} 
                                    label="Video" 
                                />
                                <FormControlLabel 
                                    value="tts" 
                                    control={<Radio disabled={true} />} 
                                    label="TTS" 
                                />
                            </RadioGroup>
                        )}
                    />
                </FormControl>

                {/* Show uploaded media */}
                {mediaFile && (
                    <>
                        <Typography variant="subtitle2" mt={2}>
                            Uploaded {normalizedMediaType === 'tts' || normalizedMediaType === 'audio' 
                                ? 'Audio' 
                                : normalizedMediaType === 'video' 
                                    ? 'Video' 
                                    : normalizedMediaType}
                        </Typography>
                        <Box mt={1}>
                            {normalizedMediaType === 'image' && (
                                <img
                                    src={mediaUrl}
                                    alt="Uploaded"
                                    style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8 }}
                                />
                            )}
                            {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && viewModeAudioUrl && (
                                <audio
                                    controls
                                    style={{ width: '100%', maxWidth: '500px' }}
                                    preload="metadata"
                                    key={`audio-${safeLang}-${tabIndex}-${normalizedMediaType}-${currentMedia.url || 'empty'}`}
                                >
                                    <source src={viewModeAudioUrl} type="audio/mpeg" />
                                    <source src={viewModeAudioUrl} type="audio/wav" />
                                    <source src={viewModeAudioUrl} type="audio/mp3" />
                                    Your browser does not support the audio element.
                                </audio>
                            )}
                            {normalizedMediaType === 'video' && viewModeVideoUrl && (
                                <video
                                    ref={videoRef}
                                    controls
                                    style={{ width: '100%', maxWidth: '500px', borderRadius: 8 }}
                                    key={`video-view-${safeLang}-${viewModeVideoUrl}`}
                                    preload="metadata"
                                >
                                    <source src={viewModeVideoUrl} type="video/mp4" />
                                    <source src={viewModeVideoUrl} type="video/webm" />
                                    <source src={viewModeVideoUrl} type="video/ogg" />
                                    Your browser does not support the video element.
                                </video>
                            )}
                        </Box>
                    </>
                )}

                {/* Show uploaded image for TTS/Audio if imageFile prop is provided */}
                {imageFile && (normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && (
                    <Box mt={2}>
                        <Typography variant="subtitle2">Uploaded Image</Typography>
                        <Box mt={1}>
                            {typeof imageFile === 'string' ? (
                                <img
                                    src={imageFile}
                                    alt="Uploaded"
                                    style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8 }}
                                />
                            ) : imageFile[0] ? (
                                <img
                                    src={URL.createObjectURL(imageFile[0])}
                                    alt="Uploaded"
                                    style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8 }}
                                />
                            ) : null}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    }

    // Edit Mode
    return (
        <Box mt={2}>
            {/* Media Type Selection */}
            <FormControl component="fieldset">
                <FormLabel component="legend">Select Media Type</FormLabel>
                <Controller
                    name="mediaType"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup
                            row
                            value={mediaType || ''}
                            onChange={(e) => {
                                if (viewMode) return; // Prevent changes in view mode
                                
                                const newType = e.target.value as MediaType;
                                
                                // Prevent changing to image type if language is not English
                                if (newType === 'image' && safeLang !== 'en') {
                                    return;
                                }
                                
                                // Check if there's uploaded media that will be lost
                                const hasUploadedMedia = 
                                    // Check mediaByLanguage for any language
                                    Object.keys(mediaByLanguage).some(
                                        lang => mediaByLanguage[lang]?.file || mediaByLanguage[lang]?.url
                                    ) || 
                                    // Check currentMedia
                                    currentMedia.file || currentMedia.url ||
                                    // Check mediaFile prop
                                    (mediaFile && (typeof mediaFile === 'string' || (mediaFile instanceof FileList && mediaFile.length > 0))) ||
                                    // Check TTS text (for TTS type)
                                    (normalizedMediaType === 'tts' && currentTtsText.trim()) ||
                                    // Check imageFile (for TTS/Audio with image)
                                    (imageFile && (typeof imageFile === 'string' || (imageFile instanceof FileList && imageFile.length > 0)));
                                
                                if (hasUploadedMedia && normalizedMediaType !== newType) {
                                    // Show confirmation dialog
                                    setPendingMediaType(newType);
                                    setPendingFieldOnChange(() => field.onChange);
                                    setShowMediaTypeChangeDialog(true);
                                } else {
                                    // No media to lose, change directly
                                    field.onChange(newType);
                                    onMediaTypeChange(newType);
                                }
                            }}
                        >
                            <FormControlLabel 
                                value="image" 
                                control={<Radio disabled={viewMode || (safeLang !== 'en' && mediaType === 'image')} />} 
                                label="Image" 
                            />
                            <FormControlLabel 
                                value="audio" 
                                control={<Radio disabled={viewMode || (safeLang !== 'en' && mediaType === 'image')} />} 
                                label="Audio" 
                            />
                            <FormControlLabel 
                                value="video" 
                                control={<Radio disabled={viewMode || (safeLang !== 'en' && mediaType === 'image')} />} 
                                label="Video" 
                            />
                            <FormControlLabel 
                                value="tts" 
                                control={<Radio disabled={viewMode || (safeLang !== 'en' && mediaType === 'image')} />} 
                                label="TTS" 
                            />
                        </RadioGroup>
                    )}
                />
            </FormControl>

            {/* Confirmation Dialog for Media Type Change */}
            <Dialog open={showMediaTypeChangeDialog} onClose={() => setShowMediaTypeChangeDialog(false)}>
                <DialogTitle>Confirm Media Type Change</DialogTitle>
                <DialogContent>
                    <Typography>
                        Your uploaded media will be lost. Are you sure you want to change the media type?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowMediaTypeChangeDialog(false);
                        setPendingMediaType(null);
                        setPendingFieldOnChange(null);
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={() => {
                            if (pendingMediaType !== null && pendingFieldOnChange) {
                                // Clear all media data in this component
                                // Revoke all blob URLs before clearing
                                Object.values(videoBlobUrls).forEach(url => {
                                    if (url?.startsWith('blob:')) {
                                        URL.revokeObjectURL(url);
                                    }
                                });
                                Object.values(imageBlobUrls).forEach(url => {
                                    if (url?.startsWith('blob:')) {
                                        URL.revokeObjectURL(url);
                                    }
                                });
                                setMediaByLanguage({});
                                setVideoBlobUrls({});
                                setImageBlobUrls({});
                                setCurrentMedia({});
                                setCurrentTtsText('');
                                setViewModeAudioUrl(null);
                                setViewModeVideoUrl(null);
                                
                                // Clear all media in parent component
                                onMediaFileChange(null);
                                if (onImageFileChange) {
                                    onImageFileChange(null);
                                }
                                
                                // Change media type (this will also clear language-specific files in parent)
                                pendingFieldOnChange(pendingMediaType);
                                onMediaTypeChange(pendingMediaType);
                                
                                setShowMediaTypeChangeDialog(false);
                                setPendingMediaType(null);
                                setPendingFieldOnChange(null);
                            }
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TTS Text Input */}
            {normalizedMediaType === 'tts' && (
                <Box mt={2}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Enter text to convert to speech"
                        value={currentTtsText}
                        onChange={(e) => {
                            const newText = e.target.value;
                            setCurrentTtsText(newText);
                            setMediaByLanguage(prev => ({
                                ...prev,
                                [safeLang]: { 
                                    ...prev[safeLang], 
                                    ttsText: newText,
                                    url: undefined,
                                    label: undefined
                                }
                            }));
                        }}
                        placeholder="Type your text here..."
                        sx={{ mb: 2 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleConvertToSpeech}
                        disabled={!currentTtsText.trim() || isConvertingTts}
                        sx={{ mb: 2 }}
                    >
                        {isConvertingTts ? "Converting..." : "Convert to Speech"}
                    </Button>
                </Box>
            )}

            {/* File Upload */}
            {((normalizedMediaType === 'image' && safeLang === 'en') || normalizedMediaType === 'audio') && (
                <Controller
                    name="mediaFile"
                    control={control}
                    render={({ field }) => (
                        <Box display="flex" alignItems="center" gap={2} mb={2} mt={2}>
                            <Button variant="outlined" component="label">
                                {buttonLabel}
                                <input
                                    type="file"
                                    accept={acceptTypes}
                                    hidden
                                    onChange={e => {
                                        field.onChange(e.target.files);
                                        handleFileUpload(e.target.files);
                                    }}
                                />
                            </Button>
                            {normalizedMediaType === 'image' && (
                                <Typography color="error">*</Typography>
                            )}
                            {!normalizedMediaType && (
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    (Optional)
                                </Typography>
                            )}
                            {currentMedia.label && (
                                <Typography variant="body2">{currentMedia.label}</Typography>
                            )}
                        </Box>
                    )}
                />
            )}
            
            {/* Video Upload */}
            {normalizedMediaType === 'video' && (
                <Controller
                    name="mediaFile"
                    control={control}
                    render={({ field }) => (
                        <Box display="flex" alignItems="center" gap={2} mb={2} mt={2}>
                            <Button variant="outlined" component="label">
                                Upload Video
                                <input
                                    type="file"
                                    accept="video/*"
                                    hidden
                                    onChange={e => {
                                        field.onChange(e.target.files);
                                        handleFileUpload(e.target.files);
                                    }}
                                />
                            </Button>
                            {currentMedia.label && (
                                <Typography variant="body2">{currentMedia.label}</Typography>
                            )}
                        </Box>
                    )}
                />
            )}


            {/* Image Upload for Audio/Video/TTS - Only show for English language */}
            {onImageFileChange && (normalizedMediaType === 'audio' || normalizedMediaType === 'tts') && safeLang === 'en' && (
                <Box mt={2}>
                    <Button variant="outlined" component="label">
                        Upload Image
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={e => onImageFileChange(e.target.files)}
                        />
                    </Button>
                    {imageFile && (
                        <>
                            <Typography variant="body2" mt={1}>
                                {typeof imageFile === 'string' ? imageFile : imageFile[0]?.name}
                            </Typography>
                            {typeof imageFile !== 'string' && imageFile[0] && (
                                <img
                                    src={URL.createObjectURL(imageFile[0])}
                                    alt="Preview"
                                    style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8, marginTop: 8 }}
                                />
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Preview Section */}
            {currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="subtitle2" mb={1}>Preview:</Typography>
                    
                    {normalizedMediaType === 'image' && (
                        <img
                            src={currentMedia.url}
                            alt="Preview"
                            style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8 }}
                        />
                    )}
                    {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && currentMedia.url && (
                        <Box>
                            {currentMedia.label && <Typography variant="body2" mb={1}>{currentMedia.label}</Typography>}
                            <audio
                                ref={audioRef}
                                controls
                                style={{ width: '100%', maxWidth: '500px' }}
                                key={`audio-${safeLang}-${tabIndex}-${normalizedMediaType}-${currentMedia.url || 'empty'}`}
                                preload="metadata"
                            >
                                <source src={currentMedia.url} type="audio/mpeg" />
                                <source src={currentMedia.url} type="audio/wav" />
                                <source src={currentMedia.url} type="audio/mp3" />
                                Your browser does not support the audio element.
                            </audio>
                        </Box>
                    )}

                    {normalizedMediaType === 'video' && currentMedia.url && mediaByLanguage[safeLang]?.file && (
                        <Box>
                            {currentMedia.label && <Typography variant="body2" mb={1}>{currentMedia.label}</Typography>}
                            <video
                                ref={videoRef}
                                controls
                                style={{ width: '100%', maxWidth: '500px', borderRadius: 8 }}
                                key={`video-${safeLang}-${tabIndex}-${currentMedia.url || 'empty'}`}
                                preload="metadata"
                            >
                                <source src={currentMedia.url} type="video/mp4" />
                                <source src={currentMedia.url} type="video/webm" />
                                <source src={currentMedia.url} type="video/ogg" />
                                Your browser does not support the video element.
                            </video>
                        </Box>
                    )}
                </Box>
            )}
            
            {/* Show message when image type is selected but not English language */}
            {normalizedMediaType === 'image' && safeLang !== 'en' && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        Image upload is only available for English language.
                    </Typography>
                </Box>
            )}
            
            {/* Show message when audio/video type is selected but no media for current language */}
            {(normalizedMediaType === 'tts' || normalizedMediaType === 'audio') && !currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No audio uploaded for {language.toUpperCase()} language yet.
                    </Typography>
                </Box>
            )}
            {normalizedMediaType === 'video' && !currentMedia.url && (
                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        No video uploaded for {language.toUpperCase()} language yet.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};